'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { ArrowRight, Check, Pencil, UserRound } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { ATAJOS_SISTEMAS, HomeAtajoCard } from './HomeAtajoCard';
import { HomeSectionTitle } from './HomeSectionTitle';
import { Reveal } from './Reveal';

/**
 * Qué se considera "perfil completo".
 *
 * Los seis puntos son los que la ficha pública MUESTRA: si falta uno, se nota al
 * visitarla. Por eso no entran acá el RUT ni el teléfono, que son privados, ni
 * las publicaciones, que hoy no se cargan desde el perfil.
 *
 * El peso no es parejo a propósito: la foto y la biografía son lo primero que ve
 * quien abre la ficha, y una ficha sin ellas se lee como vacía aunque tenga el
 * resto. Este reparto es la primera versión visible del puntaje de completitud
 * que va a decidir quién aparece en la vitrina de la portada, y ahí conviene que
 * pese lo mismo.
 */
const CRITERIOS = [
	{ id: 'foto', peso: 20, label: 'la foto de perfil', corto: 'Foto' },
	{ id: 'biografia', peso: 20, label: 'la biografía', corto: 'Biografía' },
	{ id: 'formacion', peso: 15, label: 'tu formación', corto: 'Formación' },
	{ id: 'lineas', peso: 15, label: 'una línea de investigación', corto: 'Líneas' },
	{ id: 'trayectoria', peso: 15, label: 'tu experiencia o cargos', corto: 'Trayectoria' },
	{ id: 'identificadores', peso: 15, label: 'tu ORCID o Scopus', corto: 'ORCID o Scopus' },
] as const;

type CriterioId = (typeof CRITERIOS)[number]['id'];

interface PerfilResumen {
	porcentaje: number;
	faltantes: { id: CriterioId; label: string }[];
	/** Los criterios que aplican a esta persona. Ver `criteriosPara`. */
	aplicables: typeof CRITERIOS[number][];
}

/**
 * Estamentos que NO adscriben líneas de investigación.
 *
 * A un funcionario no se le puede pedir una línea, así que ese criterio no se le
 * muestra NI le descuenta porcentaje: con el criterio incluido, su ficha nunca
 * podía llegar a 100 aunque estuviera completa, y la portada le reprochaba algo
 * que no está en su mano resolver.
 *
 * El puntaje se calcula sobre el peso de los criterios que sí aplican, no sobre
 * 100 fijo. Si esa persona pasa a académico, la línea vuelve a contar sola y su
 * porcentaje se recalcula: no hay nada guardado que corregir.
 */
const SIN_LINEAS: string[] = ['funcionario'];

const criteriosPara = (tipoInvestigador: string | null | undefined) =>
	SIN_LINEAS.includes(tipoInvestigador ?? '')
		? CRITERIOS.filter((criterio) => criterio.id !== 'lineas')
		: [...CRITERIOS];

/** Respuesta del perfil público, acotada a lo que este cálculo mira. */
interface PerfilPublico {
	investigator?: {
		fotoPerfilUrl?: string | null;
		orcid?: string | null;
		scopusAuthorId?: string | null;
		wosResearcherId?: string | null;
		tipoInvestigador?: string | null;
	};
	perfil?: { biografia?: string | null };
	lineas?: unknown[];
	cv?: { formacion?: unknown[]; experiencia?: unknown[]; cargos?: unknown[] };
}

function resumir(perfil: PerfilPublico): PerfilResumen {
	const inv = perfil.investigator ?? {};
	const cv = perfil.cv ?? {};

	const cumple: Record<CriterioId, boolean> = {
		foto: Boolean(inv.fotoPerfilUrl),
		biografia: Boolean(perfil.perfil?.biografia?.trim()),
		formacion: (cv.formacion?.length ?? 0) > 0,
		lineas: (perfil.lineas?.length ?? 0) > 0,
		trayectoria: (cv.experiencia?.length ?? 0) > 0 || (cv.cargos?.length ?? 0) > 0,
		identificadores: Boolean(inv.orcid || inv.scopusAuthorId || inv.wosResearcherId),
	};

	const aplicables = criteriosPara(inv.tipoInvestigador);
	const pesoTotal = aplicables.reduce((total, c) => total + c.peso, 0);
	const obtenido = aplicables.reduce((total, c) => total + (cumple[c.id] ? c.peso : 0), 0);

	return {
		// Sobre el peso de lo que aplica, no sobre 100: si no, quien no puede tener
		// líneas quedaría con un techo del 85 %.
		porcentaje: pesoTotal === 0 ? 100 : Math.round((obtenido / pesoTotal) * 100),
		faltantes: aplicables.filter((c) => !cumple[c.id]).map(({ id, label }) => ({ id, label })),
		aplicables,
	};
}

/** "la foto, la biografía y una línea" — sin comas de más ni "y" suelto. */
function enumerar(labels: string[]) {
	if (labels.length === 1) return labels[0];
	return `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`;
}

/**
 * Banda de cierre para quien YA tiene sesión: en qué estado está su ficha y qué
 * le falta, con el acceso directo para completarla.
 *
 * Reemplaza a la invitación a registrarse, que a un usuario con cuenta no le
 * dice nada. Y ataca el problema real del portal: hoy casi ningún perfil tiene
 * foto, biografía ni líneas cargadas, así que las fichas públicas se ven vacías.
 *
 * El cálculo se hace en el CLIENTE con el perfil público del propio usuario. No
 * viaja en el HTML servido a propósito: la portada es una página cacheada con
 * ISR y compartida por todos, así que el estado de una persona concreta no puede
 * formar parte de ella. Mientras la petición viaja no se pinta nada, en vez de
 * un esqueleto que parpadee.
 */
export function HomePerfilEstado({ slug }: { slug: string }) {
	const [resumen, setResumen] = useState<PerfilResumen | null>(null);

	useEffect(() => {
		let vigente = true;

		apiClient(`/investigadores/${encodeURIComponent(slug)}`)
			.then((res) => (res.ok ? res.json() : null))
			.then((perfil: PerfilPublico | null) => {
				if (vigente && perfil) setResumen(resumir(perfil));
			})
			.catch(() => {
				// Silencioso: es una ayuda, no un dato crítico de la portada.
			});

		return () => {
			vigente = false;
		};
	}, [slug]);

	if (!resumen) return null;

	const { porcentaje, faltantes, aplicables } = resumen;
	const completo = faltantes.length === 0;
	// Se nombran hasta tres pendientes: la lista entera se vuelve un párrafo y
	// deja de leerse como una tarea concreta.
	const visibles = faltantes.slice(0, 3).map((f) => f.label);
	const resto = faltantes.length - visibles.length;

	// Circunferencia de un círculo de radio 27 → largo del trazo del anillo.
	const CIRCUNFERENCIA = 2 * Math.PI * 27;

	return (
		<div className="bg-white px-6 pb-20 pt-16 sm:px-12">
			<Reveal>
				<div className="grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)] lg:items-center lg:gap-14">
					<div>
						<HomeSectionTitle icon={UserRound}>Tu perfil</HomeSectionTitle>

						<div className="mt-6 flex items-center gap-5">
							<div className="relative shrink-0">
								<svg width="72" height="72" viewBox="0 0 64 64" fill="none" aria-hidden>
									<circle cx="32" cy="32" r="27" stroke="#eef2f6" strokeWidth="6" />
									<circle
										cx="32"
										cy="32"
										r="27"
										stroke={completo ? '#6E7F1E' : '#5D8C9F'}
										strokeWidth="6"
										strokeLinecap="round"
										strokeDasharray={CIRCUNFERENCIA}
										strokeDashoffset={CIRCUNFERENCIA * (1 - porcentaje / 100)}
										transform="rotate(-90 32 32)"
									/>
								</svg>
								<span className="absolute inset-0 flex items-center justify-center text-[15px] font-bold text-[#233a4d]">
									{porcentaje}%
								</span>
							</div>
							<p className="text-[15px] leading-relaxed text-black/[0.66]">
								{completo
									? 'Tu ficha está completa. Gracias por mantenerla al día.'
									: `Te ${faltantes.length === 1 ? 'falta' : 'faltan'} ${enumerar(visibles)}${resto > 0 ? ` y ${resto} cosa${resto > 1 ? 's' : ''} más` : ''}.`}
							</p>
						</div>

						<div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-3">
							<Link
								href="/mi-perfil/editar"
								className="inline-flex items-center gap-2 rounded-[10px] bg-[#233a4d] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#233a4d]/90"
							>
								<Pencil size={16} aria-hidden />
								{completo ? 'Editar mi perfil' : 'Completar mi perfil'}
							</Link>
							<Link
								href={`/investigadores/${slug}`}
								className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#354857] transition hover:underline"
							>
								Ver mi ficha pública
								<ArrowRight size={15} aria-hidden />
							</Link>
						</div>
					</div>

					{/* Los seis puntos, para ver de un vistazo qué falta sin entrar al
					    editor. Lista, pero SIN caja ni filetes: eran ellos los que la hacían
					    pesar tanto como la invitación de la izquierda, que es la que debe
					    mandar. Lo que distingue cumplido de pendiente es el disco: relleno
					    en el verde institucional con su tilde, o un círculo punteado vacío.
					    Un punteado se lee como "acá falta algo" mejor que un tilde gris. */}
					{completo && ATAJOS_SISTEMAS.length > 0 ? (
						<div className="flex flex-col gap-2.5">
							{ATAJOS_SISTEMAS.map((atajo) => (
								<HomeAtajoCard key={atajo.label} {...atajo} />
							))}
						</div>
					) : (
					<ul className="flex flex-col gap-3">
						{aplicables.map(({ id, corto }) => {
							const pendiente = faltantes.some((f) => f.id === id);
							return (
								<li key={id} className="flex items-center gap-3">
									<span
										className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full ${
											pendiente ? 'border border-dashed border-[#a8c0cb]' : 'bg-[#6E7F1E]'
										}`}
									>
										{!pendiente && (
											<Check size={13} strokeWidth={3} aria-hidden className="text-white" />
										)}
									</span>
									<span
										className={`text-[14px] ${
											pendiente ? 'text-[#5b7180]' : 'font-medium text-[#233a4d]'
										}`}
									>
										{corto}
									</span>
								</li>
							);
						})}
					</ul>
					)}
				</div>
			</Reveal>
		</div>
	);
}
